import React, { useState } from 'react'
import {
  Box,
  TextField,
  FormHelperText,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
import UploadIcon from '@mui/icons-material/Upload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  useGetDealerLuckyDrawCanSubmit,
  usePostDealerLuckyDraw,
} from '../../../api/promotions/dealer/dealer-lucky-draw'
import { useNavigate } from 'react-router-dom'

const MAX_FILE_SIZE = 5 // MB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg']

const StyledFieldBox = styled(Box)`
  && {
    max-width: 800px;
    justify-content: space-evenly;
    min-height: 80px;
    margin: auto;
    display: flex;
    flex-direction: column;
  }
`

const LuckyDrawForm = () => {
  const { register, handleSubmit, control, setValue, setError } = useForm({
    mode: 'onChange',
  })

  const navigate = useNavigate()
  const { mutateAsync, isLoading } = usePostDealerLuckyDraw()

  const { data } = useGetDealerLuckyDrawCanSubmit()

  const { enqueueSnackbar } = useSnackbar()

  const [openDialog, setopenDialog] = useState(false)

  const onSubmit = async (data) => {
    let formData = new FormData()

    for (let key in data) {
      // Add files
      if (['scratchCardPhoto'].includes(key)) {
        formData.append(key, data[key][0])
      } else {
        formData.append(key, data[key])
      }
    }

    try {
      const res = await mutateAsync(formData)

      setopenDialog(true)

      return res
    } catch (error) {
      // Display error on popup
      enqueueSnackbar(error?.response?.data?.message ?? 'Submit fail', {
        variant: 'error',
        autoHideDuration: 7000,
      })
    }
  }

  const handleClose = () => {
    // Close dialog
    setopenDialog(false)

    // Navigate back to home page
    navigate('/')

    // Scroll back to top
    window.scrollTo(0, 0)
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledFieldBox>
          <Controller
            name='businessName'
            control={control}
            defaultValue=''
            rules={{
              required: 'Business Name is required',
              maxLength: { value: 200, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Business Name'
                fullWidth
                value={value}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='phone'
            control={control}
            defaultValue=''
            rules={{
              required: 'Phone number is required',
              pattern: {
                value: /^(601)[0-46-9]-*[0-9]{7,8}$/,
                message: `Invalid phone number. Phone number should follow the format of 601xxxxxxxx. '+' sign is not required`,
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Phone Number'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                placeholder='60127123456'
              />
            )}
          />
        </StyledFieldBox>

        <StyledFieldBox>
          <Controller
            name='cardNumber'
            control={control}
            defaultValue=''
            rules={{
              required: 'Card Number is required',
              pattern: {
                value: /^\d{6}$/,
                message: 'Invalid card number format - xxxxxx',
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const handleChange = (event) => {
                const sanitizedValue = event.target.value.replace(/\s/g, '').toUpperCase()
                onChange(sanitizedValue)
              }
              return (
                <TextField
                  label='Card Number'
                  value={value}
                  onChange={handleChange}
                  error={!!error}
                  fullWidth
                  helperText={error ? error.message : null}
                />
              )
            }}
          />
        </StyledFieldBox>

        <StyledFieldBox display='flex' flexDirection='column'>
          {/* Sample for users to used as reference */}
          <Typography variant='h2' mt={2} mb={2}>
            Sample of the scratch card
          </Typography>
          <img
            src='/assets/promotions/dealer/lucky-draw/sample.png'
            style={{ maxWidth: '400px' }}
          />
          <Box mt={4}>
            <Controller
              name='scratchCardPhoto'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('scratchCardPhoto')
                // React Hook Form does not block field value being set on failing validation
                // This is a workaround by manually handling both the file size validation and value assignment
                const handleOnChange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2

                  const fileType = e?.target?.files[0]?.type

                  let errorMsg = null

                  // Validate file size
                  if (fileMb > MAX_FILE_SIZE) {
                    // Set error message
                    errorMsg = 'File size must be 5 MB or smaller'
                  } else if (!ALLOWED_FILE_TYPES.includes(fileType)) {
                    // Validate file type
                    errorMsg = 'Only PNG and JPG files are allowed'
                  }

                  // If there are errors
                  if (errorMsg) {
                    // Set error
                    setError(props.name, {
                      type: 'manual',
                      message: errorMsg,
                    })

                    // Reset field value
                    setValue(props.name, null)
                  } else {
                    // Set field value
                    props.onChange(e)
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload a photo of the scratch card
                      <input
                        type='file'
                        accept={ALLOWED_FILE_TYPES}
                        hidden
                        {...props}
                        onChange={handleOnChange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>
        <StyledFieldBox>
          <Box my={2}>
            <Typography variant='h4'>
              * One (1) lucky draw entry is strictly limited to one (1) scratch card number.
            </Typography>
            <Typography variant='h4'>
              * The scratch cards are randomly placed in VARTA® and Energizer® battery boxes. You
              must keep your scratch card and show the serial number to redeem your prize.
            </Typography>
            <Typography variant='h4'>
              * Lucky draw entries submitted from 1/2/2026 to 31/5/2026 will be drawn in June 2026.
            </Typography>
            <Typography variant='h4'>
              * The QR code for dealers will be available for scanning to enter the lucky draw until
              31 May 2026.
            </Typography>
          </Box>
          <Box>
            <Button
              fullWidth
              type='submit'
              size='large'
              variant='contained'
              disabled={isLoading || !data?.allow}
              endIcon={isLoading && <CircularProgress style={{ color: '#fff' }} size='1rem' />}
            >
              Submit
            </Button>
            <Typography variant='body2' color='error' mt={1}>
              {(() => {
                // If not allowed, return the message
                if (!data?.allow) return data?.message
                else if (isLoading) return 'Please wait while we process your submission.'
                else return null
              })()}
            </Typography>
          </Box>
        </StyledFieldBox>
      </form>
      <Dialog open={openDialog} onClose={handleClose}>
        <DialogContent dividers>
          <Box display='flex' justifyContent='flex-start' alignItems='center' pb={3}>
            <CheckCircleIcon style={{ color: '#4caf50' }} />
            <Typography variant={'h3'}>{'Form Submitted.'}</Typography>
          </Box>
          <Typography gutterBottom>Redirecting back to homepage...</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default LuckyDrawForm
